import { useState } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'

import {
  ApproveTrainingRequestDatatableDocument,
  RequestDetailComponentEmployeesDocument,
  TrainingRequestDetailFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, FormProvider } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { RequestDetailComponent } from 'modules/trainings/components/MyTrainings/RequestsComponent/RequestDetailComponent'
import { FooterDraft } from 'modules/trainings/components/MyTrainings/RequestsComponent/RequestDetailComponent/FooterDraft'
import { FooterReview } from 'modules/trainings/components/MyTrainings/RequestsComponent/RequestDetailComponent/FooterReview'
import { sumMinutesToHours } from 'modules/trainings/components/MyTrainings/RequestsComponent/RequestDetailComponent/useHoursAndMinutes'
import { useDeleteTrainingRequest } from 'modules/trainings/hooks/TrainingRequests/useDeleteTrainingRequest'
import { useRejectTrainingRequest } from 'modules/trainings/hooks/TrainingRequests/useRejectTrainingRequest'
import { useUpdateTrainingRequest } from 'modules/trainings/hooks/TrainingRequests/useUpdateTrainingRequest'
import { RequestType, UpdateData } from 'modules/trainings/pages/MyTrainings/RequestDetail/types'

// TODO: The state of this component is being passed arround a lot.
// We should refactor to use a useReducer hook and manage state as a state machine
function RequestDetail({ back }: { back: string }) {
  const { reject, update, approve, updating, approving, rejecting, deleteTrainingRequest } =
    useMutations()
  const {
    isApprover,
    showApproveOptions,
    trainingRequest,
    selectedRequestType,
    setSelectedRequestType,
    isUpdating,
    setIsUpdating,
  } = useRequestDetailState()
  const { company } = useCurrentSession()
  const navigate = useNavigate()
  const { data: permissionsData } = useQuery(RequestDetailComponentEmployeesDocument, {
    variables: {
      trainingRequestId: trainingRequest.id,
    },
  })

  const manageEmployee = !!permissionsData?.trainings.trainingRequests.find(
    (tr) => tr.id === trainingRequest.id
  )?._policies?.manageRequest.can

  return (
    <FormProvider
      defaultValues={{
        id: trainingRequest.id,
        status: trainingRequest.status,
        competencyIds: trainingRequest.competencyIds,
        employeeIds: trainingRequest.employeeIds,
        subsidized: trainingRequest.subsidized,
        trainingName: trainingRequest.trainingName || '',
        trainingNeed: trainingRequest.trainingNeed || '',
        trainingId: trainingRequest.training?.id || undefined,
        externalReference: trainingRequest.externalReference || '',
        comments: trainingRequest.comments || '',
        startDate: trainingRequest.startDate || undefined,
        endDate: trainingRequest.endDate || undefined,
        cost: Number(trainingRequest.cost),
        duration: (() => {
          if (!trainingRequest.duration) return undefined
          return Math.floor(Number(trainingRequest.duration))
        })(),
        minutes: (() => {
          if (!trainingRequest.duration) return undefined
          const minutes =
            Number(trainingRequest.duration) - Math.floor(Number(trainingRequest.duration))
          return Math.round(minutes * 60)
        })(),
        schedule: trainingRequest.schedule || undefined,
        providerName: trainingRequest.providerName || undefined,
      }}
      validate={(values) => {
        const errors: FormErrors<UpdateData> = {}
        if (!values.trainingId && selectedRequestType === 'fromCatalog') {
          errors.trainingId = i18n.t('common.empty')
        }
        if (!values.trainingName) {
          errors.trainingName = i18n.t('common.empty')
        }
        if (!values.trainingNeed) {
          errors.trainingNeed = i18n.t('common.empty')
        }
        return errors
      }}
      onSubmit={async (data) => {
        const result = await update({
          ...data,
          duration: sumMinutesToHours(data.duration, data.minutes),
          status: data.status,
        })
        const handled = handleResult(result, () => {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('trainings.training_requests.create.wrong'),
          })
        })
        if (handled.success) {
          app.setFlash({
            type: 'correct',
            message: isUpdating
              ? i18n.t('trainings.training_requests.create.success_messages.save')
              : i18n.t('trainings.training_requests.create.success_messages.pending'),
          })
          navigate(back)
        }
        return handled
      }}
    >
      <>
        <ResponsivePage.Body showHeader={false}>
          <RequestDetailComponent
            back={back}
            trainingRequest={trainingRequest}
            selectedRequestType={selectedRequestType}
            setSelectedRequestType={setSelectedRequestType}
            isApprover={isApprover}
            manageEmployee={manageEmployee}
          />
        </ResponsivePage.Body>
        {trainingRequest.status == 'review' && (
          <FooterReview
            setIsUpdating={setIsUpdating}
            manageEmployee={manageEmployee}
            showApproveOptions={!!showApproveOptions}
            onApprove={async () => {
              const result = await approve({
                variables: {
                  id: trainingRequest.id,
                  companyId: company.id,
                },
              })
              const handled = handleResult(result, () => {
                app.setFlash({
                  type: 'wrong',
                  message: i18n.t('trainings.training_requests.create.wrong'),
                })
              })
              if (handled.success) {
                app.setFlash({
                  type: 'correct',
                  message: i18n.t('trainings.training_requests.create.success_messages.approved'),
                })
                track(trackingEvents.training.trainingRequest.approveTrainingRequest.submit, {
                  is_successful: handled.success,
                })
                navigate(back)
              }
            }}
            onReject={async (reason: string) => {
              const result = await reject({
                id: trainingRequest.id,
                companyId: company.id,
                reason: reason,
              })
              const handled = handleResult(result, () => {
                app.setFlash({
                  type: 'wrong',
                  message: i18n.t('trainings.training_requests.create.wrong'),
                })
              })
              track(trackingEvents.training.trainingRequest.rejectTrainingRequest.submit, {
                is_successful: handled.success,
              })
              if (handled.success) {
                app.setFlash({
                  type: 'correct',
                  message: i18n.t('trainings.training_requests.create.success_messages.rejected'),
                })
                navigate(back)
              }
            }}
            approving={approving}
            rejecting={rejecting}
          />
        )}
        {trainingRequest.status == 'draft' && (
          <FooterDraft
            setIsUpdating={setIsUpdating}
            manageEmployee={manageEmployee}
            onDelete={async () => {
              const result = await deleteTrainingRequest({
                id: trainingRequest.id,
              })
              const handled = handleResult(result, () => {
                app.setFlash({
                  type: 'wrong',
                  message: i18n.t('trainings.training_requests.create.wrong'),
                })
              })
              if (handled.success) {
                app.setFlash({
                  type: 'correct',
                  message: i18n.t('trainings.training_requests.create.delete'),
                })
                navigate(back)
              }
            }}
            updating={updating}
            deleting={false}
          />
        )}
      </>
    </FormProvider>
  )
}

function useMutations() {
  const { delete: deleteTrainingRequest } = useDeleteTrainingRequest()
  const { update, loading: updating } = useUpdateTrainingRequest()
  const { reject, loading: rejecting } = useRejectTrainingRequest()
  const [approve, { loading: approving }] = useMutation(ApproveTrainingRequestDatatableDocument, {
    extractorFn: (data) => data.trainingsMutations.approveTrainingsTrainingRequest,
  })
  return { deleteTrainingRequest, update, reject, approve, updating, rejecting, approving }
}

function useRequestDetailState() {
  const { trainingRequest } = useOutletContext<{
    trainingRequest: TrainingRequestDetailFragment
    loadingResources: boolean
  }>()
  const {
    access: { id: currentAccessId },
  } = useCurrentSession()
  const [selectedRequestType, setSelectedRequestType] = useState<RequestType>(
    trainingRequest.training?.id ? 'fromCatalog' : 'newRequest'
  )
  const [isUpdating, setIsUpdating] = useState<boolean>(false)
  const isApprover = trainingRequest.currentApprovers
    .map((approver) => approver.id)
    .includes(currentAccessId)
  const showApproveOptions = trainingRequest.status === 'review' && isApprover
  return {
    trainingRequest,
    selectedRequestType,
    setSelectedRequestType,
    isUpdating,
    setIsUpdating,
    isApprover,
    showApproveOptions,
  }
}

export default RequestDetail
